import { Option } from "fp-ts/Option";
import { Subdomain } from "../utils/subdomain/subdomain";
import { option } from "fp-ts";
import { useQuery } from "../utils/supabase/useQuery";
import { clubsTable, organizationClubsView } from "../domain/club";
import { column, columns } from "../utils/pg-ts/pg-ts";
import * as t from "io-ts";
import { pipe } from "fp-ts/function";
import { omit, pick } from "../utils/fp";
import { OrganizationId } from "../domain/organization";
import { resultsTable } from "../domain/result";

const organizationClubCols = pipe(
    organizationClubsView.columns,
    pick("id", "title", "ignored", "results_count")
);
const organizationClubCodec = t.type(organizationClubCols, "OrganizationClub");
export type OrganizationClub = t.TypeOf<typeof organizationClubCodec>;

export const useOrganizationClubs = (orgSubdomain: Option<Subdomain>) => {
    const subdomainNullable = option.toNullable(orgSubdomain);
    const shouldSkipQuery = option.isNone(orgSubdomain);
    return useQuery(
        `clubs_${subdomainNullable}`,
        (supabase) =>
            supabase
                .from(organizationClubsView.name)
                .select(columns(organizationClubCols))
                .eq(
                    column(organizationClubsView, "subdomain"),
                    subdomainNullable
                )
                .order(column(organizationClubsView, "title")),
        t.array(organizationClubCodec),
        undefined,
        shouldSkipQuery
    );
};

const autocompleteClubCols = pipe(clubsTable.columns, pick("title"));
const autocompleteClubCodec = t.type(
    autocompleteClubCols,
    "clubAutocompleteItem"
);
export const useOrganizationClubsForAutocomplete = (
    organizationId: Nullable<OrganizationId>
) => {
    return useQuery(
        `clubsAutocomplete_${organizationId}`,
        (supabase) =>
            supabase
                .from(clubsTable.name)
                .select(columns(autocompleteClubCols))
                .eq(column(clubsTable, "organization_id"), organizationId)
                .eq(column(clubsTable, "ignored"), false)
                .order(column(clubsTable, "title")),
        t.array(autocompleteClubCodec),
        undefined,
        organizationId == null
    );
};

export const clubUpdateInputCodec = t.intersection(
    [
        // all fields are optional, except id
        pipe(clubsTable.columns, pick("id"), (cols) =>
            t.type(cols, "required")
        ),
        pipe(omit(clubsTable.columns, "id", "organization_id"), (cols) =>
            t.partial(cols, "optional")
        ),
    ],
    "ClubUpdateInput"
);
export type ClubUpdateInput = t.TypeOf<typeof clubUpdateInputCodec>;
